import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

export default function InvitationSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 overflow-hidden">
      {/* Sophisticated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-950 to-navy-900"></div>
      
      {/* Overlay Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #d4b07e 1px, transparent 0)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-champagne-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blush-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <div className={`space-y-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Badge */}
          <div className="inline-block">
            <div className="px-6 py-2 bg-champagne-500/10 border border-champagne-400/30 backdrop-blur-sm rounded-full">
              <span className="text-champagne-400 text-sm font-sans font-medium tracking-[0.2em] uppercase">
                By Application Only
              </span>
            </div>
          </div>

          {/* Heading */}
          <div className="space-y-6">
            <h2 className="font-display text-5xl lg:text-7xl font-bold text-white leading-[1.1]">
              Invitation
            </h2>
            
            <div className="w-20 h-0.5 bg-champagne-400 mx-auto"></div>
          </div>

          {/* Description */}
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-2xl lg:text-3xl font-display text-white italic leading-relaxed">
              A curated room, by application only.
            </p>
            
            <p className="text-lg text-navy-200 font-sans leading-relaxed">
              Personally curated by the <strong className="text-white font-semibold">WealthRise Management team</strong> to ensure alignment in values, ambition, and discretion.
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8">
            <Link
              to="/attend"
              className="group relative px-10 py-5 bg-champagne-500 text-navy-950 font-sans font-semibold text-lg overflow-hidden transition-all duration-300 hover:bg-champagne-400 hover:shadow-2xl hover:shadow-champagne-500/20 whitespace-nowrap cursor-pointer"
            >
              <span className="relative z-10 flex items-center gap-3">
                Request an Invitation
                <i className="ri-arrow-right-line text-xl"></i>
              </span>
            </Link>
            
            <Link
              to="/attend"
              className="group px-10 py-5 border-2 border-navy-400 text-white font-sans font-semibold text-lg hover:bg-navy-800 hover:border-champagne-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-3">
                Nominate a Participant
                <i className="ri-user-add-line text-xl"></i>
              </span>
            </Link>
          </div>

          {/* Bottom Features */}
          <div className={`grid md:grid-cols-3 gap-8 pt-16 max-w-4xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="space-y-3">
              <div className="w-12 h-12 flex items-center justify-center bg-champagne-500/10 rounded-full mx-auto">
                <i className="ri-shield-check-line text-2xl text-champagne-400"></i>
              </div>
              <h3 className="font-sans font-semibold text-white">Curated Selection</h3>
              <p className="text-sm text-navy-300 font-sans">Every participant is personally reviewed</p>
            </div>

            <div className="space-y-3">
              <div className="w-12 h-12 flex items-center justify-center bg-champagne-500/10 rounded-full mx-auto">
                <i className="ri-lock-line text-2xl text-champagne-400"></i>
              </div>
              <h3 className="font-sans font-semibold text-white">Complete Discretion</h3>
              <p className="text-sm text-navy-300 font-sans">Privacy and confidentiality assured</p>
            </div>

            <div className="space-y-3">
              <div className="w-12 h-12 flex items-center justify-center bg-champagne-500/10 rounded-full mx-auto">
                <i className="ri-star-line text-2xl text-champagne-400"></i>
              </div>
              <h3 className="font-sans font-semibold text-white">Exceptional Value</h3>
              <p className="text-sm text-navy-300 font-sans">Alignment in ambition and vision</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}